<template>
  <VcWidget class="order-1 lg:order-none">
    <div class="flex flex-col justify-start">
      <div class="">
        <VcButton
          type="button"
          variant="link"
          size="lg"
          class="optional-fields-section !flex w-full items-center gap-2 text-base !font-bold !text-[--color-neutral-a1]"
          @click="toggleSection"
        >
          {{ $t("common.labels.optional_fields") }}
          <VcIcon
            :name="isOptionalFieldsExpanded ? 'chevron-down' : 'chevron-up'"
            class="mr-2 stroke-white stroke-2 text-[--color-neutral-a1]"
            :size="24"
          />
        </VcButton>
      </div>
      <div v-show="!isOptionalFieldsExpanded" class="mt-4 flex flex-col gap-8 border-t pt-4 md:mx-[-20px] md:px-5">
        <div v-if="shouldShowTaxExempt">
          <div class="flex items-center gap-[10px]">
            <p class="font-semibold">{{ $t("common.placeholders.tax_exempt_no") }}</p>
            <VcTooltip placement="right" hover width="max-content">
              <template #trigger>
                <VcIcon name="information-circle" :size="18" />
              </template>
              <template #content>
                {{ $t("shared.checkout.tax_exempt_info") }}
              </template>
            </VcTooltip>
          </div>
          <VcInput
            v-model.trim="taxExemptNo"
            type="text"
            :placeholder="$t('shared.checkout.tax_exempt_placeholder')"
            class="option-input mt-2.5 w-full"
            @blur="saveTaxExemptNo"
          />
        </div>

        <div>
          <p class="font-semibold">{{ $t("common.labels.po_reference") }}</p>
          <VcInput
            v-model.trim="poNumber"
            type="text"
            :maxlength="30"
            :placeholder="$t('common.labels.po_reference')"
            class="option-input mt-2.5 w-full"
          >
          </VcInput>
        </div>

        <div v-if="displayOptionalInvoiceFields">
          <div class="flex flex-col gap-3">
            <p class="font-semibold leading-6">{{ $t("common.labels.email_invoice") }}</p>
            <VcCheckbox
              v-if="currencySettings.invoicePDF"
              id="invoicePdf"
              v-model="invoicePdf"
              name="emailInvoice"
              class="rounded-md text-base font-medium"
              :label="$t('common.labels.invoice_pdf')"
            >
              {{ $t("common.labels.invoice_pdf") }}
            </VcCheckbox>
            <VcCheckbox
              v-if="currencySettings.proformaInvoice"
              id="proformaInvoice"
              v-model="proformaInvoice"
              name="emailInvoice"
              class="rounded-md text-base font-medium"
              :label="$t('common.labels.proforma_invoice')"
            >
              {{ $t("common.labels.proforma_invoice") }}
            </VcCheckbox>
          </div>
        </div>

        <div>
          <p class="font-semibold">{{ $t("common.labels.comments") }}</p>
          <VcTextarea
            v-model.trim="commentText"
            :placeholder="$t('common.labels.comments_text')"
            class="mt-2.5 w-full"
            rows="4"
            :max-length="1000"
            counter
          ></VcTextarea>
        </div>
      </div>
      <div v-show="isOptionalFieldsExpanded" class="mt-2.5 font-medium leading-6 text-[--color-neutral-a2]">
        <span>{{ $t("common.labels.collapse_optional_fields") }}</span>
      </div>
    </div>
  </VcWidget>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { eligibleVatCountries } from "@/core/constants/eligible-vat-countries";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import { useUser, useUserAddresses } from "@/shared/account/composables";
import { useCountryISO3 } from "@/shared/common/composables/useCountryISO3";
import { eventBus } from "@/shared/thorlabs/shared/event-bus";
import { useCheckoutFacade } from "../composables/useCheckoutFacade";
import { currencyConfig } from "../currency-settings";
import type { CurrencySettingsType } from "../types";

const isOptionalFieldsExpanded = ref(false);

const { isAuthenticated } = useUser();
const {
  poNumber,
  commentText,
  invoicePdf,
  proformaInvoice,
  deliveryAddress,
  setCartDeliveryAddress,
  addresses,
  userSelectedAddress,
} = useCheckoutFacade();
const { addOrUpdateAddresses } = useUserAddresses();

const taxExemptNo = ref(deliveryAddress.value?.taxExemptNoTL ?? "");

const shouldShowTaxExempt = computed(() => {
  if (!isAuthenticated.value) {
    return false;
  }
  const code = deliveryAddress.value?.countryCode;
  if (!code) {
    return false;
  }
  const iso3 = code.length === 3 ? code : useCountryISO3(code);
  return eligibleVatCountries.includes(iso3);
});

watch(
  () => deliveryAddress.value?.taxExemptNoTL,
  (newVal) => {
    taxExemptNo.value = newVal ?? "";
  },
);

const saveTaxExemptNo = async () => {
  const currentAddress = addresses.value.find((a) => a.id === userSelectedAddress.value);
  if (!currentAddress) {
    return;
  }

  const currentTaxExemptNo = (currentAddress.taxExemptNoTL ?? "").trim();
  if (taxExemptNo.value === currentTaxExemptNo) {
    return;
  }

  const updatedAddress = { ...currentAddress, taxExemptNoTL: taxExemptNo.value };

  try {
    await setCartDeliveryAddress(updatedAddress);
    await addOrUpdateAddresses([updatedAddress]);
    eventBus.emit("hasTaxExemptNumber", { hasTaxExemptNumber: !!taxExemptNo.value });
  } catch (e) {
    Logger.error("optional-fields.saveTaxExemptNo", e);
  }
};

const toggleSection = () => {
  isOptionalFieldsExpanded.value = !isOptionalFieldsExpanded.value;
};

const currencySettings = currencyConfig[globals.currencyCode] as CurrencySettingsType;

const displayOptionalInvoiceFields = computed(() => {
  return currencySettings.proformaInvoice && currencySettings.invoicePDF;
});
</script>

<style scoped lang="scss">
:deep(.vc-input-details__counter) {
  @apply text-sm leading-[22px] text-[--color-neutral-a3];
}
:deep(.vc-textarea) {
  @apply gap-0;
}
</style>
